import { useState } from 'react';
import { humanizeKey } from '../workflowBuilder/definitionEdit.js';
import type { ApiKey, DeclaredField, TenantEventType } from '../../lib/types.js';

export interface EventDefinitionFormProps {
  /** The held row being approved, or null when declaring a new type. */
  entry: TenantEventType | null;
  /** Catalog scope keys the tenant may grant. */
  scopeOptions: { value: string; label: string }[];
  /** Everything already in the catalog, so the name is checked as it is typed
   *  rather than failing on submit. */
  existing: TenantEventType[];
  onSubmit: (input: {
    type: string;
    acknowledgeRemovedFields?: string[];
    description: string;
    scopes: string[];
    examplePayload?: Record<string, unknown>;
    processHeld?: boolean;
    /** The declared list — the allowlist a step scope is projected against. */
    fields?: DeclaredField[];
    /** A doorbell event: it carries no data by design. */
    signalOnly?: boolean;
  }) => Promise<unknown>;
  isBusy: boolean;
  error: Error | null;
  /** Keys that may post events, shown on the handoff. */
  emitKeys: ApiKey[];
  /** Shown after registering, so a new type comes with something to hand on.
   *  Null while the page is still the definition form. */
  registered: TenantEventType | null;
  onDone: () => void;
  onBuildWorkflow: (entry: TenantEventType) => void;
  /** Sends one synthetic event of the just-registered type. */
  onSendTest: (entry: TenantEventType) => Promise<unknown>;
}

const SLUG = /^[a-z0-9_]{1,40}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

/** "3 hours" / "2 days" — plain enough to judge staleness at a glance. */
export const ageOf = (d: Date): string => {
  const mins = Math.max(1, Math.round((Date.now() - d.getTime()) / 60_000));
  if (mins < 60) return `${String(mins)} minute${mins === 1 ? '' : 's'}`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${String(hours)} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  return `${String(days)} day${days === 1 ? '' : 's'}`;
};

export const useEventDefinitionLogic = ({
  entry,
  existing,
  onSubmit,
  isBusy,
  registered,
  error,
}: EventDefinitionFormProps) => {
  // Keyed remount (see the caller) gives a fresh form per target, so these
  // initialise from `entry` once rather than syncing to it in an effect.
  const [slug, setSlug] = useState(() => entry?.type.replace(/^custom\./, '') ?? '');
  const [description, setDescription] = useState(() => entry?.description ?? '');
  const [scopes, setScopes] = useState<string[]>(() => entry?.scopes ?? []);

  // The DECLARED list — the allowlist, and the only thing the author edits.
  // Seeded from what was already declared, or from observation's suggestion
  // the first time round. Observation counts stay server-side: they are facts
  // about traffic, not values to submit back.
  // Nothing declared means nothing has been curated, so everything observed
  // starts IN — the sender's fields are the suggestion. Once a list exists it
  // is the author's, and observation can only offer the rest as chips: an
  // allowlist traffic can re-broaden is not an allowlist.
  const [fields, setFields] = useState<DeclaredField[]>(() =>
    entry?.declaredFields?.fields.length
      ? entry.declaredFields.fields.map((f) => ({ ...f }))
      : (entry?.shape?.fields ?? []).map((f) => ({
          name: f.name,
          type: f.type,
          required: f.required,
        })),
  );
  const [newField, setNewField] = useState('');
  const [addingField, setAddingField] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [confirmRemoval, setConfirmRemoval] = useState(false);
  const [warnedFields, setWarnedFields] = useState<string[]>([]);
  const [fieldsDirty, setFieldsDirty] = useState(false);
  const editField = (name: string, patch: Partial<DeclaredField>) => {
    setFields((prev) => prev.map((f) => (f.name === name ? { ...f, ...patch } : f)));
    setFieldsDirty(true);
    setConfirmRemoval(false);
  };
  const dropField = (name: string) => {
    setFields((prev) => prev.filter((f) => f.name !== name));
    setFieldsDirty(true);
    setConfirmRemoval(false);
    if (editingField === name) setEditingField(null);
  };
  const addField = (name: string, type: DeclaredField['type']) => {
    setFields((prev) => [...prev, { name, type, required: false }]);
    setEditingField(name);
    setFieldsDirty(true);
    setConfirmRemoval(false);
  };

  // Narrowing the payload to the declared list. Off by default — a type
  // registered before anyone curated its fields must not start dropping data.

  // Ticked for a type genuinely declared as carrying no data. An empty declared
  // list alongside OBSERVED fields is a different thing: a sender started
  // sending after a signal-only registration, and the fields are waiting to be
  // confirmed, not a doorbell decision. Defaulting on there would hide the
  // editor and offer a checkbox for a state nobody chose.
  const [signalOnly, setSignalOnly] = useState(
    () => entry?.declaredFields?.fields.length === 0 && !entry.shape?.fields.length,
  );
  const [example, setExample] = useState('');

  // What arrived while the type was held is stored and replayable. Ticked by
  // default only when the whole set is recent: a days-old alarm firing a
  // notification sends someone to look at a unit that has been fine since.
  const heldCount = entry?.heldCount ?? 0;
  const oldestHeld = entry?.oldestHeldAt ? new Date(entry.oldestHeldAt) : null;
  const heldIsFresh = oldestHeld !== null && Date.now() - oldestHeld.getTime() < DAY_MS;
  const [processHeld, setProcessHeld] = useState(() => heldIsFresh);

  const footerNote = !entry
    ? 'It won’t do anything until something sends it.'
    : heldCount === 0
      ? ''
      : processHeld
        ? `The ${String(heldCount)} events already received will run too.`
        : `The ${String(heldCount)} events already received stay held.`;

  const isEditing = entry?.status === 'approved';
  const isNew = !entry;
  const shape = entry?.shape ?? null;
  const fullType = `custom.${slug}`;
  const charsOk = slug.length > 0 && SLUG.test(slug);

  // Checked as you type against the catalog we already hold, so a clash is
  // visible before submitting rather than as a 409 afterwards. The row's own
  // status makes the message useful: a HELD clash has a better path than
  // registering it fresh.
  const clash = isNew ? existing.find((t) => t.type === fullType) : undefined;
  const slugValid = charsOk && !clash;

  // A pasted example only counts if it parses to a JSON object — an array or
  // a bare value has no fields to read.
  const parsedExample = (() => {
    if (signalOnly || example.trim() === '') return { ok: true as const, value: undefined };
    try {
      const v: unknown = JSON.parse(example);
      if (!v || typeof v !== 'object' || Array.isArray(v)) {
        return { ok: false as const, message: 'Paste a JSON object, like {"unit_id": "2078", …}' };
      }
      return { ok: true as const, value: v as Record<string, unknown> };
    } catch {
      return { ok: false as const, message: 'That isn’t valid JSON.' };
    }
  })();

  const detectFields = () => {
    if (!parsedExample.ok || !parsedExample.value) return;
    const detected = Object.entries(parsedExample.value)
      .slice(0, 100)
      .map(([name, value]) => {
        const type: DeclaredField['type'] =
          value === null
            ? 'null'
            : Array.isArray(value)
              ? 'array'
              : typeof value === 'number'
                ? 'number'
                : typeof value === 'boolean'
                  ? 'boolean'
                  : typeof value === 'string'
                    ? /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/.test(
                        value,
                      )
                      ? 'date'
                      : 'string'
                    : 'object';
        return { name, type, required: false };
      });
    setFields(detected);
    setFieldsDirty(true);
    setConfirmRemoval(false);
    setEditingField(null);
  };

  // A workflow only gets declared fields, so registering with none is
  // registering something that can hand a workflow nothing. The one exception
  // is the deliberate doorbell case.
  const hasObserved = Boolean(entry?.shape && entry.shape.fields.length > 0);
  // `signalOnly` is a valid answer either way: declaring nothing on purpose is
  // a decision, not an empty form.
  const somethingToDeclare =
    signalOnly ||
    fields.length > 0 ||
    (!hasObserved && !fieldsDirty && Boolean(parsedExample.value));

  const choicesValid = fields.every(
    (f) =>
      !f.values ||
      (f.values.length > 0 &&
        f.values.length <= 50 &&
        f.values.every((v) => v.trim().length > 0 && v.trim().length <= 200) &&
        new Set(f.values.map((v) => v.trim())).size === f.values.length),
  );
  const removedFields = (entry?.declaredFields?.fields ?? entry?.shape?.fields ?? [])
    .filter((field) => signalOnly || !fields.some((next) => next.name === field.name))
    .map((field) => field.name)
    .sort();
  const removalWarning = Boolean(
    error &&
    'code' in error &&
    error.code === 'event_fields_in_use' &&
    warnedFields.length > 0 &&
    JSON.stringify(warnedFields) === JSON.stringify(removedFields),
  );
  const submit = () => {
    if (isBusy || registered) return;
    if (!slugValid || !parsedExample.ok || !somethingToDeclare || (!signalOnly && !choicesValid))
      return;
    void onSubmit({
      type: fullType,
      ...(confirmRemoval && removalWarning ? { acknowledgeRemovedFields: warnedFields } : {}),
      description,
      scopes,
      examplePayload: parsedExample.value,
      // The empty list is what makes it a doorbell; `signalOnly` alone only
      // unlocks the guard that would otherwise reject it.
      ...(signalOnly ? { signalOnly: true, fields: [] } : {}),
      ...(heldCount > 0 ? { processHeld } : {}),
      // After signalOnly, so an edited list cannot re-add fields the doorbell
      // choice just cleared.
      ...(fieldsDirty && !signalOnly
        ? {
            fields: fields.map((f) => ({
              ...f,
              label: f.label?.trim() || humanizeKey(f.name).slice(0, 80),
              ...(f.values ? { values: f.values.map((v) => v.trim()) } : {}),
            })),
          }
        : {}),
    }).catch((caught: unknown) => {
      if (caught instanceof Error && 'code' in caught && caught.code === 'event_fields_in_use') {
        setWarnedFields(removedFields);
        setConfirmRemoval(false);
      }
    });
  };

  return {
    removalWarning,
    confirmRemoval,
    setConfirmRemoval,
    slug,
    setSlug,
    description,
    setDescription,
    scopes,
    setScopes,
    fields,
    editingField,
    setEditingField,
    detectFields,
    editField,
    dropField,
    addField,
    newField,
    setNewField,
    addingField,
    setAddingField,
    signalOnly,
    setSignalOnly,
    example,
    setExample,
    heldCount,
    oldestHeld,
    processHeld,
    setProcessHeld,
    footerNote,
    isEditing,
    isNew,
    shape,
    charsOk,
    clash,
    slugValid,
    parsedExample,
    hasObserved,
    somethingToDeclare,
    choicesValid,
    submit,
  };
};
